import React, { useEffect, useRef, useState } from "react";
import { apiGet, apiPost } from "../../../Api/Client.js";
import { safeHttpUrl } from "../../../Api/safeHttpUrl.js";
import {
  Play, ArrowUpRight, ArrowDownRight, Activity, EyeOff, MessageSquare, ShieldOff,
  Building2, CalendarClock, Clock, Users, Ban, History, ExternalLink, BookOpen, ClipboardList,
  ListChecks, ChevronDown, ChevronUp, PencilLine, Flag, Info,
} from "lucide-react";
import { DynamicsStrip } from "./BandGauge.jsx";
import ContentSignals from "./ContentSignals.jsx";
import CallPlayer, { isPlayable, playHref, stamp } from "./CallPlayer.jsx";
import {
  callAverage, companyOf, durationOf, sameCompany, formatDate, formatDuration, formatStartTime,
  callTypeLabel, isRefused, participantsOf, refusalLabel, scoreColor,
} from "./callMeta.js";

// One scored call, written for the rep rather than for the database.
//
// The pipeline's natural output is scoring telemetry — f0_variance_hz2,
// sentiment: 58, a bare 4/10. Nobody has ever changed a behaviour because of
// any of those, and "58 out of what, and so what?" has no answer. What ships to
// a person whose commission depends on it has to read like a coach talking.
//
// The rules this component is the enforcement point for:
//
//   1. No raw metric reaches the screen. Show the behaviour it implies.
//      (Grep this file for f0, rms, voiced_ratio: nothing.)
//   2. Every score carries the rep's own words and a timestamp that plays the
//      call at that moment. A score you can't inspect is a score you can only
//      argue with — this is the biggest trust lever available here.
//   3. Prescribe, don't diagnose. `try_next` is a line they can say.
//   4. One priority per call. Eight scores is a report; one fix is a drill.
//   5. Strength first, and specific, because it decides whether the rest gets
//      read at all.
//   7. Describe the behaviour on this call, never the person.
//   8. Silence beats invention — "no objection came up" is a real sentence.
//
// And rule 11, which is the reputational one: the rubric's `zero` anchors are
// blunt by design ("Rambles, hides behind a name", "Caves the moment the buyer
// pushes"). That register is right for defining a standard and wrong aimed at a
// named person as a verdict in the HR system of record. Only `ten` is rendered,
// as a target. There is deliberately no path in this file that displays `zero`.

const card = {
  background: "var(--surface)", border: "1px solid var(--c-200)",
  borderRadius: 14, padding: "16px 18px",
};
const muted = { color: "var(--c-500)" };

// The timestamp beside a quote. Where the recording can be embedded it seeks
// the player on this card; where it can't, it stays the external media-fragment
// link it always was. Both are honest and they look different on purpose —
// a control that seeks and a control that opens a tab should not be the same
// affordance.
function Timestamp({ atSeconds, callLink, onSeek }) {
  if (atSeconds == null) return null;
  const shared = {
    ...muted, fontSize: 11, textDecoration: "none", display: "inline-flex",
    alignItems: "center", gap: 3,
  };

  if (isPlayable(callLink)) {
    return (
      <button type="button"
              onClick={() => onSeek && onSeek(atSeconds)}
              title={`Play from ${stamp(atSeconds)}`}
              style={{ ...shared, border: "none", background: "transparent", cursor: "pointer",
                       padding: 0, fontFamily: "inherit", color: "var(--blue-600)", fontWeight: 600 }}>
        <Play size={10} /> {stamp(atSeconds)}
      </button>
    );
  }

  const href = playHref(callLink, atSeconds);
  return href ? (
    <a href={href} target="_blank" rel="noreferrer" style={shared}>
      <Play size={10} /> {stamp(atSeconds)}
    </a>
  ) : (
    <span style={{ ...muted, fontSize: 11 }}>▸ {stamp(atSeconds)}</span>
  );
}

function Quote({ heard, atSeconds, callLink, onSeek, speaker }) {
  if (!heard) return null;
  return (
    <blockquote style={{
      margin: "5px 0 4px", padding: "6px 0 6px 10px",
      borderLeft: "2px solid var(--c-300)", fontSize: 12.5, lineHeight: 1.55,
      color: "var(--c-800)", fontStyle: "italic",
    }}>
      “{heard}”
      {/* Verified as said on the call, but not by the rep — the buyer, or a
          colleague on a multi-party call. Presenting it as the rep's own line
          would be putting words in their mouth. */}
      {speaker && (
        <span style={{ fontStyle: "normal", fontSize: 11, ...muted, marginLeft: 6 }}>
          — said by {speaker}
        </span>
      )}
      <span style={{ fontStyle: "normal", marginLeft: 8, whiteSpace: "nowrap" }}>
        <Timestamp atSeconds={atSeconds} callLink={callLink} onSeek={onSeek} />
      </span>
    </blockquote>
  );
}

// The floor: hygiene the Skill Universe checks on every call (apology and
// learner language, stacked questions, talking over your own question). The
// target for each is zero, so a count is a fact, not a verdict -- no band, no
// colour judgement beyond "clean" or "N times". Every count carries the words
// and the second, so the rep can play it back. Counted by rule, not by the
// model (ECHO/floor_checks.py).
function FloorChecks({ checks, callLink, onSeek }) {
  if (!Array.isArray(checks) || checks.length === 0) return null;
  const hits = checks.filter((c) => c.count > 0);
  return (
    <Section icon={ListChecks} tone="var(--c-600)" title="Floor checks"
             right={<span style={{ fontSize: 10, ...muted }}>target: zero on every call</span>}>
      {hits.length === 0 && (
        <p style={{ margin: 0, fontSize: 12.5, color: "var(--green-600)" }}>
          Clean on all {checks.length}: {checks.map((c) => c.label.toLowerCase()).join(", ")}.
        </p>
      )}
      {hits.map((c) => (
        <div key={c.skill} style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 12.5, color: "var(--c-800)" }}>
            <strong>{c.label}</strong>
            <span style={{ ...muted, fontSize: 11 }}> · {c.skill} · {c.count} time{c.count === 1 ? "" : "s"}</span>
          </div>
          {(c.examples || []).map((ex, i) => (
            <Quote key={i} heard={ex.heard} atSeconds={ex.at_seconds} callLink={callLink} onSeek={onSeek} />
          ))}
        </div>
      ))}
      {hits.length > 0 && hits.length < checks.length && (
        <p style={{ ...muted, fontSize: 11, margin: "2px 0 0" }}>
          Clean: {checks.filter((c) => c.count === 0).map((c) => c.label.toLowerCase()).join(", ")}.
        </p>
      )}
    </Section>
  );
}

// A score can stand on ONE kind of evidence and still have had another kind
// attached and removed -- a tonality score rests on its acoustic measure, but
// the model volunteers a quote regardless, and when that quote doesn't check
// out it is stripped rather than shown. Silently, that reads as "no quote was
// ever offered for this", which is a different and less trustworthy claim
// than "one was offered and didn't hold up, so it's gone." Say which.
const STRIPPED_LABEL = { quote: "a quoted line", timestamp: "its timestamp", acoustic: "an acoustic citation" };

function StrippedEvidenceNote({ stripped }) {
  if (!stripped || !stripped.length) return null;
  return (
    <p style={{ margin: "4px 0 0", fontSize: 11, color: "var(--c-500)", display: "flex",
               alignItems: "flex-start", gap: 4 }}>
      <ShieldOff size={11} style={{ marginTop: 1.5, flexShrink: 0 }} />
      The score stands, but {stripped.map((s) => STRIPPED_LABEL[s] || s).join(" and ")} the
      model attached to it could not be verified against the recording, so{" "}
      {stripped.length > 1 ? "they were" : "it was"} removed rather than shown.
    </p>
  );
}

// Where the Playbook teaches this parameter. The rubric says what good
// looks like; the playbook already says how to get there, in the company's
// own words, and until now the two never met on the card.
export function PlaybookLine({ link }) {
  // A skill the playbook doesn't teach (pace, fillers) links to its law only;
  // a link with neither a step nor a law shows nothing rather than filler.
  if (!link || (!link.substep && !link.law)) return null;
  const sub = link.substep || {};
  return (
    <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, border: "1px solid var(--c-200)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10.5, color: "var(--c-500)",
                    textTransform: "uppercase", letterSpacing: ".04em" }}>
        <BookOpen size={11} /> From the playbook
        {link.substep ? ` · ${link.stage?.name || ""} → ${sub.number} ${sub.title}` : ""}
        {link.substep && link.playbook && link.playbook !== "Universal" ? ` (${link.playbook})` : ""}
      </div>
      {sub.best_practice && (
        <div style={{ marginTop: 3, fontSize: 12.5, color: "var(--c-800)", lineHeight: 1.5 }}>{sub.best_practice}</div>
      )}
      {sub.example && (
        <div style={{ marginTop: 3, fontSize: 12, color: "var(--c-700)", fontStyle: "italic", lineHeight: 1.5 }}>
          e.g. {sub.example}
        </div>
      )}
      {link.law?.name && (
        <div style={{ marginTop: 4, fontSize: 11.5, color: "var(--c-600)" }}>
          <strong>{link.law.name}.</strong> {link.law.body}
        </div>
      )}
      <a href="/playbook/" style={{ display: "inline-block", marginTop: 4, fontSize: 11.5, color: "var(--blue-600)",
                                    textDecoration: "none", fontWeight: 600 }}>
        Open the playbook →
      </a>
    </div>
  );
}

// A score a coach changed says so, wherever it appears: from what, by whom,
// and why. A rep reading a number a person changed must not mistake it for
// the recording's verdict.
export function AdjustedNote({ param }) {
  const a = param?.adjusted;
  if (!a) return null;
  return (
    <div style={{ marginTop: 3, fontSize: 11.5, color: "var(--c-600)", display: "flex", gap: 5,
                  alignItems: "flex-start" }}>
      <PencilLine size={12} style={{ marginTop: 2, flexShrink: 0 }} />
      <span>
        Adjusted by {a.by || "a coach"} from {a.from ?? "not scored"}
        {a.note ? <span style={{ color: "var(--c-700)" }}> — “{a.note}”</span> : null}
      </span>
    </div>
  );
}

const DISPUTE_COPY = {
  open: { text: "You disputed this — waiting for your coach", tone: "var(--blue-600)" },
  upheld: { text: "Your coach kept this score", tone: "var(--c-700)" },
  adjusted: { text: "Your coach adjusted this score", tone: "var(--green-600)" },
};

function ScoreRow({ call, param, dispute, canAdjust, canDispute, onAdjusted, onDisputed }) {
  const [mode, setMode] = useState(null);          // null | "adjust" | "dispute"
  const [value, setValue] = useState(param.score == null ? "" : String(param.score));
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const score = param.score;

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      if (mode === "adjust") {
        const res = await apiPost(`/RevOps/SellerCallScores/${call.id}/adjust/`, {
          scores: { [param.key]: value === "" ? null : Number(value) }, reason: text,
        });
        onAdjusted && onAdjusted(res.call);
      } else {
        await apiPost("/RevOps/ScoreDisputes/", { call: call.id, parameter_key: param.key, reason: text });
        onDisputed && onDisputed();
      }
      setMode(null);
      setText("");
    } catch (err) {
      setError(err?.message || "That didn't go through.");
    } finally {
      setBusy(false);
    }
  };

  const status = dispute && DISPUTE_COPY[dispute.status];
  return (
    <div style={{ padding: "8px 0", borderTop: "1px solid var(--c-200)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--c-900)" }}>{param.label}</span>
        <span style={{ display: "inline-flex", gap: 10, alignItems: "baseline", flexShrink: 0 }}>
          {canAdjust && mode === null && (
            <button type="button" onClick={() => setMode("adjust")} style={linkButton}>
              <PencilLine size={11} /> Adjust
            </button>
          )}
          {canDispute && mode === null && !(dispute && dispute.status === "open") && (
            <button type="button" onClick={() => setMode("dispute")} style={linkButton}>
              <Flag size={11} /> Dispute
            </button>
          )}
          <strong style={{ fontSize: 13, color: scoreColor(score), fontVariantNumeric: "tabular-nums" }}>
            {score == null ? "—" : `${score}/10`}
          </strong>
        </span>
      </div>
      {param.heard ? (
        <div style={{ marginTop: 2, fontSize: 12, color: "var(--c-700)", fontStyle: "italic" }}>
          {param.quote_speaker
            ? `${param.quote_speaker.charAt(0).toUpperCase()}${param.quote_speaker.slice(1)} said `
            : ""}“{param.heard}”
        </div>
      ) : null}
      {param.why && <div style={{ marginTop: 2, fontSize: 12, color: "var(--c-700)" }}>{param.why}</div>}
      <AdjustedNote param={param} />
      {status && (
        <div style={{ marginTop: 3, fontSize: 11.5, color: status.tone }}>
          {status.text}
          {dispute.resolution_note ? <span style={{ color: "var(--c-700)" }}> — “{dispute.resolution_note}”</span> : null}
        </div>
      )}

      {mode && (
        <div style={{ marginTop: 6, padding: "8px 10px", borderRadius: 8, border: "1px solid var(--c-200)",
                      display: "flex", flexDirection: "column", gap: 6 }}>
          {mode === "adjust" && (
            <label style={{ fontSize: 12, color: "var(--c-700)", display: "flex", gap: 8, alignItems: "center" }}>
              New score
              <select value={value} onChange={(e) => setValue(e.target.value)}
                      style={{ padding: "3px 6px", borderRadius: 6, border: "1px solid var(--c-200)",
                               background: "var(--surface)", color: "var(--c-900)" }}>
                {[...Array(11).keys()].map((n) => <option key={n} value={String(n)}>{n}</option>)}
                <option value="">Not seen on this call</option>
              </select>
            </label>
          )}
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
                    placeholder={mode === "adjust"
                      ? "Why is it changing? The rep will read this."
                      : "What did the score miss? e.g. that line was the buyer's, not mine."}
                    style={{ width: "100%", fontSize: 12.5, padding: "6px 8px", borderRadius: 7,
                             border: "1px solid var(--c-200)", background: "var(--surface)",
                             color: "var(--c-900)", fontFamily: "inherit", resize: "vertical" }} />
          {error && <div style={{ fontSize: 11.5, color: "var(--red-600, #c0392b)" }}>{error}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" disabled={busy || !text.trim()} onClick={submit}
                    style={{ ...linkButton, border: "1px solid var(--blue-600)", padding: "4px 10px", borderRadius: 7,
                             opacity: busy || !text.trim() ? 0.5 : 1 }}>
              {mode === "adjust" ? "Save the new score" : "Send to my coach"}
            </button>
            <button type="button" onClick={() => { setMode(null); setError(""); }}
                    style={{ ...linkButton, color: "var(--c-500)" }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Who the buyer was, on the Skill Universe's tier bridge. It decides which
// skills this call is graded on (spec R1): an SMB founder is not a test of an
// L6 reframe. A coach sets it; a rep reads it. Blank means everything is
// graded, as before -- nobody is gated on a guess.
export const BUYER_TIERS = [
  ["rfp", "Specced RFP shopper"],
  ["smb_founder", "SMB founder"],
  ["mid_mgr", "Mid-market manager"],
  ["mid_exec", "Mid-market exec"],
  ["vp", "Senior / VP"],
  ["ceo", "CEO peer"],
];

function BuyerTier({ call, canSet, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const hidden = call.not_graded_at_this_tier || 0;
  if (!canSet && !call.buyer_tier) return null;

  const change = async (value) => {
    setBusy(true);
    setError("");
    try {
      const res = await apiPost(`/RevOps/SellerCallScores/${call.id}/buyer-tier/`, { buyer_tier: value });
      onChanged && onChanged(res);
    } catch (err) {
      setError(err?.message || "That didn't go through.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 6, fontSize: 12, color: "var(--c-700)", display: "flex", gap: 8,
                  alignItems: "center", flexWrap: "wrap" }}>
      <span style={{ ...muted }}>Buyer</span>
      {canSet ? (
        <select value={call.buyer_tier || ""} disabled={busy} onChange={(e) => change(e.target.value)}
                style={{ padding: "2px 6px", borderRadius: 6, border: "1px solid var(--c-200)",
                         background: "var(--surface)", color: "var(--c-900)", fontSize: 12 }}>
          <option value="">Not set — every skill graded</option>
          {BUYER_TIERS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>
      ) : (
        <strong style={{ fontWeight: 600 }}>{call.buyer_tier_label}</strong>
      )}
      {hidden > 0 && (
        <span style={{ ...muted, fontSize: 11.5 }}>
          · {hidden} skill{hidden === 1 ? "" : "s"} not graded for this buyer
        </span>
      )}
      {error && <span style={{ fontSize: 11.5, color: "var(--red-600, #c0392b)" }}>{error}</span>}
    </div>
  );
}

const LAYER_NAMES = {
  L0: "AI as infrastructure", L1: "reading the people", L2: "finding the real problem",
  L3: "building the deal", L4: "keeping deals moving", L5: "how you communicate",
  L6: "reframing what they believe", L7: "not needing the deal", L8: "inner calm",
};

// Where the fix usually lives (spec R2). The failure shows in the words, the
// cause is often lower down -- a feature dump is usually discovery that never
// found the pain. Only rendered when the root is a different layer from the
// symptom; otherwise it would just repeat the section above.
function RootOfTheFix({ callId, parameterKey }) {
  const [fb, setFb] = useState(null);
  useEffect(() => {
    let live = true;
    apiGet(`/RevOps/SellerCallScores/${callId}/feedback/`)
      .then((res) => { if (live) setFb(res?.feedback || null); })
      .catch(() => {});
    return () => { live = false; };
  }, [callId, parameterKey]);
  if (!fb || fb.parameter !== parameterKey || !fb.drill_note) return null;
  if (fb.root_layer_hypothesis === fb.symptom_layer) return null;
  return (
    <div style={{ fontSize: 12.5, color: "var(--c-800)", marginTop: 6 }}>
      <span style={{ ...muted, fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".04em" }}>
        Where the fix usually lives
      </span>
      <div style={{ marginTop: 2 }}>
        {LAYER_NAMES[fb.root_layer_hypothesis] || fb.root_layer_hypothesis}
        {fb.root_confirmed ? "" : <span style={{ ...muted }}> (likely, not measured on this call)</span>}
        {" — "}{fb.drill_note}
      </div>
    </div>
  );
}

const linkButton = {
  border: "none", background: "transparent", padding: 0, cursor: "pointer", fontFamily: "inherit",
  fontSize: 11.5, fontWeight: 600, color: "var(--blue-600)", display: "inline-flex",
  alignItems: "center", gap: 4,
};

// Every scored skill on the call -- the two sections above show only the best
// and the one to fix. This is also where a coach corrects a score and where a
// rep disputes one: a score with no route from "I disagree" to a person was
// the open risk on a number that lands in the HR record.
function AllScores({ call, observed, disputes, canAdjust, canDispute, onAdjusted, onDisputed }) {
  const [open, setOpen] = useState(false);
  if (!observed.length) return null;
  const byKey = {};
  (disputes || []).filter((d) => d.status !== "withdrawn").forEach((d) => {
    if (!byKey[d.parameter_key] || d.status === "open") byKey[d.parameter_key] = d;
  });
  const openCount = Object.values(byKey).filter((d) => d.status === "open").length;
  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--c-200)" }}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
              style={{ ...linkButton, color: "var(--c-600)", fontSize: 11, fontWeight: 700,
                       letterSpacing: ".04em", textTransform: "uppercase" }}>
        <ListChecks size={13} /> Every score on this call ({observed.length})
        {openCount ? <span style={{ color: "var(--blue-600)", textTransform: "none", letterSpacing: 0 }}>
          · {openCount} disputed</span> : null}
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
      </button>
      {open && (
        <div style={{ marginTop: 4 }}>
          {canDispute && (
            <p style={{ margin: "4px 0 2px", fontSize: 11.5, color: "var(--c-500)" }}>
              Think a score is wrong? Dispute it and your coach will look at the moment and answer you.
            </p>
          )}
          {observed.map((p) => (
            <ScoreRow key={p.key} call={call} param={p} dispute={byKey[p.key]} canAdjust={canAdjust}
                      canDispute={canDispute} onAdjusted={onAdjusted} onDisputed={onDisputed} />
          ))}
        </div>
      )}
    </div>
  );
}

// The supporting detail of a call, folded. Everything a rep acts on -- the
// headline, the player, what worked, the one fix, how they sounded -- stays
// open; the context behind it (conversation measures, counts, skills that
// didn't come up, other calls with the account) sits one click away. Open, the
// card ran to a dozen sections and the one fix was a small part of a long page.
function MoreAboutThisCall({ parts, children }) {
  const [open, setOpen] = useState(false);
  if (!parts.length) return null;
  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--c-200)" }}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
              style={{ ...linkButton, color: "var(--c-600)", fontSize: 11, fontWeight: 700,
                       letterSpacing: ".04em", textTransform: "uppercase" }}>
        <Info size={13} /> More about this call
        <span style={{ color: "var(--c-500)", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
          · {parts.join(", ")}
        </span>
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
      </button>
      {open && children}
    </div>
  );
}

function Section({ icon: Icon, tone, title, right, children }) {
  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--c-200)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11,
          fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", color: tone,
        }}>
          <Icon size={13} /> {title}
        </span>
        {right}
      </div>
      <div style={{ marginTop: 5 }}>{children}</div>
    </div>
  );
}

function Fact({ icon: Icon, label, children }) {
  if (children == null || children === "" || children === false) return null;
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 10, ...muted, textTransform: "uppercase", letterSpacing: ".04em",
                    display: "flex", alignItems: "center", gap: 4 }}>
        <Icon size={11} /> {label}
      </div>
      <div style={{ fontSize: 12.5, color: "var(--c-900)", marginTop: 2 }}>{children}</div>
    </div>
  );
}

// The call itself, before any judgement of it: who it was with, when, for how
// long, and how many people were on the line. A score read without these is
// read without the one piece of context that most changes what it means — a
// 5/10 on a 70-second gatekeeper call is not the same as one on an hour-long
// demo with a buying committee.
function CallFacts({ call }) {
  const company = call.company;
  const companyName = companyOf(call);
  const start = formatStartTime(call.started_at);
  const duration = formatDuration(durationOf(call));
  const ctx = call.call_context || {};
  const people = participantsOf(call);
  // Buyer-side labels only: the rep's own colleagues (a co-presenter, a
  // manager sitting in) used to be lumped into this same list under a raw
  // diariser label like "SPEAKER_02" -- indistinguishable from an actual
  // second buyer, and never named even when the recorder knew exactly who
  // they were.
  const others = ctx.other_speakers || [];
  const colleagues = call.participants?.colleagues || [];
  const buyerInvited = call.participants?.buyer_invited;
  const buyerSpoke = call.participants?.buyer_spoke;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: 12, marginTop: 10, padding: "10px 12px", borderRadius: 10,
                  background: "var(--c-100, rgba(120,120,120,.07))" }}>
      <Fact icon={Building2} label="Company">
        {companyName && (
          <>
            <span style={{ fontWeight: 600 }}>{companyName}</span>
            {safeHttpUrl(company?.website) && (
              <a href={safeHttpUrl(company.website)} target="_blank" rel="noreferrer"
                 title={safeHttpUrl(company.website)} style={{ marginLeft: 5, color: "var(--blue-600)" }}>
                <ExternalLink size={11} />
              </a>
            )}
            {(company?.industry || company?.stage) && (
              <div style={{ fontSize: 11, ...muted }}>
                {[company.industry, company.stage && `Stage: ${company.stage}`].filter(Boolean).join(" · ")}
              </div>
            )}
            {call.project_name && <div style={{ fontSize: 11, ...muted }}>{call.project_name}</div>}
          </>
        )}
      </Fact>
      <Fact icon={CalendarClock} label="When">
        {(call.started_at || call.call_date) && `${formatDate(call)}${start ? `, ${start}` : ""}`}
      </Fact>
      <Fact icon={Clock} label="Length">
        {duration && (
          <>
            {duration}
            {ctx.short_call && (
              <div style={{ fontSize: 11, ...muted }}>
                Short call — only what it had room for is scored.
              </div>
            )}
          </>
        )}
      </Fact>
      <Fact icon={Users} label="On the call">
        {people && (
          <>
            {people} {people === 1 ? "speaker" : "people spoke"}
            {colleagues.length > 0 && (
              <div style={{ fontSize: 11, ...muted }}>
                with {colleagues.map((c) => c.name).join(", ")}
                {colleagues.length === 1 ? " (colleague)" : " (colleagues)"}
              </div>
            )}
            {others.length > 0 && (
              <div style={{ fontSize: 11, ...muted, overflow: "hidden", textOverflow: "ellipsis" }}
                   title={others.join(", ")}>
                {colleagues.length > 0 ? "and " : "with "}
                {others.slice(0, 3).join(", ")}{others.length > 3 ? ` +${others.length - 3}` : ""}
                {" "}from their side
              </div>
            )}
            {/* From the calendar invite, not the transcript: how many of the
                buyer's side never made a sound, without claiming to know
                WHICH ones -- an external voice with nothing to say leaves no
                speaker label behind to be "missing" from. */}
            {typeof buyerInvited === "number" && buyerInvited > 0 && (
              <div style={{ fontSize: 11, ...muted }}>
                {buyerInvited} invited from their side
                {typeof buyerSpoke === "number" ? `, ${buyerSpoke} spoke` : ""}.
              </div>
            )}
            {/* Matches the same rule the scoring prompt uses: multi-party is
                about the BUYER side splitting several ways, not about a
                colleague joining the rep on the call. */}
            {others.length >= 2 && (
              <div style={{ fontSize: 11, ...muted }}>
                Multi-party: talk share is judged against {Math.round(100 / people)}% each, not 50%.
              </div>
            )}
          </>
        )}
      </Fact>
    </div>
  );
}

// Every other call this rep has had with the same company. The single call is
// rarely the question; "is this deal going better than last time?" is.
function CompanyHistory({ call, history, onOpenCall }) {
  const name = companyOf(call);
  if (!name || !history?.length) return null;
  const sameCompany = history
    .filter((c) => c.id !== call.id && sameCompany(c, call))
    .sort((a, b) => (b.started_at || b.call_date || "").localeCompare(a.started_at || a.call_date || ""));
  if (!sameCompany.length) return null;

  return (
    <Section icon={History} tone="var(--c-600)" title={`Other calls with ${name}`}
             right={<span style={{ fontSize: 10, ...muted }}>{sameCompany.length} more</span>}>
      {sameCompany.slice(0, 6).map((c) => {
        const { avg } = callAverage(c);
        const refused = isRefused(c);
        return (
          <button key={c.id} type="button" onClick={() => onOpenCall && onOpenCall(c.id)}
                  style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, width: "100%",
                           textAlign: "left", padding: "6px 0", border: "none",
                           borderBottom: "1px solid var(--c-200)", background: "transparent",
                           cursor: onOpenCall ? "pointer" : "default", font: "inherit" }}>
            <span style={{ fontSize: 12, color: "var(--c-800)", minWidth: 0 }}>
              {formatDate(c)}
              <span style={muted}>
                {formatDuration(durationOf(c)) ? ` · ${formatDuration(durationOf(c))}` : ""}
                {c.call_label ? ` · ${c.call_label}` : ""}
              </span>
            </span>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: scoreColor(refused ? null : avg),
                           fontVariantNumeric: "tabular-nums" }}>
              {refused ? "not scored" : avg ?? "—"}
            </span>
          </button>
        );
      })}
    </Section>
  );
}

// `playerRef` lets the page own the player, so the voice and moments sections
// below the card seek the same recording the quotes on the card do.
export default function CallCard({ call, priorMeasures = {}, history = [], onOpenCall, playerRef: sharedRef,
                                   playbookLinks = {}, onOpenAccount, disputes = [], canAdjust = false,
                                   canDispute = false, onAdjusted, onDisputed }) {
  const ownRef = useRef(null);
  const playerRef = sharedRef || ownRef;
  const seek = (seconds) => playerRef.current?.seek(seconds);

  const parameters = call.parameters || [];
  const observed = parameters.filter((p) => p.observed !== false && p.score != null);
  // A score this buyer's tier does not grade (spec R1) is never the one fix
  // or the strength. A rep's payload doesn't carry them at all; a coach's
  // does, and they stay adjustable in "Every score".
  const graded = observed.filter((p) => p.gate !== "above_tier");
  const unobserved = parameters.filter((p) => p.observed === false || p.score == null);

  // Exactly one priority: whatever the scorer named, or the lowest observed
  // score as a fallback. Never a list — a rep shown six simultaneous failings
  // acts on none of them.
  const priority =
    graded.find((p) => p.key === call.priority) ||
    graded.reduce((worst, p) => (!worst || p.score < worst.score ? p : worst), null);
  // "What worked" only when something actually worked: the best skill has
  // to reach the rubric's target of 7. It used to be whichever skill scored
  // highest, so a call with nothing above 4 still opened on "What worked",
  // quoting the very line that made it a miss.
  const best = graded.reduce((top, p) => (!top || p.score > top.score ? p : top), null);
  const strength = best && best.score >= 7 && best !== priority ? best : null;

  const dims = call.dimension_scores || {};
  const isAi = call.source === "echo";
  // What sits behind "More about this call" -- counted the way the sections
  // themselves decide whether to render, so the summary never promises one
  // that doesn't open.
  const hasMeasures = !!call.measures && Object.keys(call.measures).length > 0;
  const company = companyOf(call);
  const otherCalls = company
    ? (history || []).filter((c) => c.id !== call.id && sameCompany(c, call)).length
    : 0;

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
        <strong style={{ fontSize: 14.5, color: "var(--c-900)" }}>{call.call_label}</strong>
        <span style={{ fontSize: 11, ...muted, whiteSpace: "nowrap" }}>
          {callTypeLabel(call)}
          {isAi ? " · scored from the recording"
            : call.source === "crm" ? " · computed from the CRM" : " · entered by hand"}
        </span>
      </div>

      <CallFacts call={call} />
      <BuyerTier call={call} canSet={canAdjust} onChanged={onAdjusted} />

      {/* The next call with this company is what a rep is actually preparing
          for. The brief gathers everything on record about them. */}
      {onOpenAccount && companyOf(call) && (
        <button type="button" onClick={() => onOpenAccount(companyOf(call))}
                style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6,
                         border: "1px solid var(--c-200)", background: "transparent", borderRadius: 8,
                         padding: "5px 10px", cursor: "pointer", fontSize: 12, fontWeight: 600,
                         color: "var(--blue-600)", fontFamily: "inherit" }}>
          <ClipboardList size={13} /> Prep for the next call with {companyOf(call)}
        </button>
      )}

      {/* A refused call is a fact about the recording, not a verdict on the
          rep, and it says so in the scorer's own words. It used to arrive as
          a card with nothing on it, which read as the system breaking. */}
      {isRefused(call) && (
        <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 10,
                      border: "1px dashed var(--c-300)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5,
                        fontWeight: 700, color: "var(--c-800)" }}>
            <Ban size={13} /> {refusalLabel(call)} — not scored
          </div>
          {call.refusal?.message && (
            <p style={{ margin: "4px 0 0", fontSize: 12.5, lineHeight: 1.55, color: "var(--c-700)" }}>
              {call.refusal.message}
            </p>
          )}
          <p style={{ margin: "4px 0 0", fontSize: 11.5, ...muted }}>
            It doesn't count for or against anyone's scorecard.
          </p>
        </div>
      )}

      {call.headline && (
        <p style={{ margin: "6px 0 0", fontSize: 13.5, color: "var(--c-800)", lineHeight: 1.5 }}>
          {call.headline}
        </p>
      )}

      {/* A coach's own free-text note — distinct from `headline` (the model's
          one-liner) and `delivery` (prose about the voice). Modelled and
          serialized since the field existed, but nowhere on the card that
          renders it, so a note left on a hand-entered row was invisible to
          the rep it was written for. */}
      {call.overall_note && (
        <p style={{ margin: "6px 0 0", padding: "7px 10px", fontSize: 12.5, lineHeight: 1.55,
                    color: "var(--c-700)", background: "var(--c-100, rgba(120,120,120,.07))",
                    borderRadius: 8 }}>
          {call.overall_note}
        </p>
      )}

      <CallPlayer ref={playerRef} link={call.call_link} label={call.call_label} />

      {(dims.tonality != null || dims.technicality != null) && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: "flex", gap: 18, alignItems: "flex-end", flexWrap: "wrap" }}>
            {[
              ["Voice & delivery", dims.tonality, dims.weights?.voice],
              ["Substance", dims.technicality, dims.weights?.substance],
            ].map(([label, value, weight]) =>
              value == null ? null : (
                <div key={label}>
                  <div style={{ fontSize: 10.5, ...muted, textTransform: "uppercase", letterSpacing: ".04em" }}>
                    {label}
                    {/* The rollup weighs these unevenly -- voice carries 60%
                        of the blended score by default, because two reps can
                        say identical words and one of them closes. Showing
                        the two figures side by side with no weight attached
                        implied they counted equally, which stopped being true
                        the moment the scorer was built this way. */}
                    {weight != null && <span> · ×{weight}</span>}
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: "var(--c-900)" }}>
                    {value}<span style={{ fontSize: 11, ...muted, fontWeight: 400 }}> / 10</span>
                  </div>
                </div>
              ),
            )}
            {dims.blended != null && (
              <div style={{ borderLeft: "1px solid var(--c-200)", paddingLeft: 18 }}>
                <div style={{ fontSize: 10.5, ...muted, textTransform: "uppercase", letterSpacing: ".04em" }}>
                  Blended
                </div>
                <div style={{ fontSize: 17, fontWeight: 700, color: "var(--blue-600)" }}>
                  {dims.blended}<span style={{ fontSize: 11, ...muted, fontWeight: 400 }}> / 10</span>
                </div>
              </div>
            )}
          </div>
          {/* Only present when one half was withheld (no usable audio, most
              often) and the weights therefore fell back onto whichever half
              WAS measured -- a call scored on substance alone should not read
              as if voice weighed in and came out neutral. */}
          {dims.weights?.applied_over?.length === 1 && (
            <p style={{ ...muted, fontSize: 11, margin: "5px 0 0" }}>
              Blended from {dims.weights.applied_over[0] === "tonality" ? "voice" : "substance"} alone —
              the other half wasn't measurable on this call.
            </p>
          )}
          {/* The blend leans toward the middle when few skills could be
              judged (ECHO/rubric.py BLEND_PRIOR), so one moment on a short
              call doesn't read as the whole call. Said out loud, because a
              Blended figure that disagrees with the only score beside it
              otherwise looks like a bug. */}
          {dims.blended_raw != null && dims.blended != null && dims.blended !== dims.blended_raw && (
            <p style={{ ...muted, fontSize: 11, margin: "5px 0 0" }}>
              Leans toward 5 when few skills could be judged —{" "}
              {dims.observed_count === 1 ? "one skill was" : `${dims.observed_count} skills were`} scored
              here (straight average {dims.blended_raw}).
            </p>
          )}
        </div>
      )}

      {strength && (
        <Section icon={ArrowUpRight} tone="var(--green-600)" title={`What worked — ${strength.label}`}>
          <Quote heard={strength.heard} atSeconds={strength.at_seconds} callLink={call.call_link} onSeek={seek}
                 speaker={strength.quote_speaker} />
          {strength.why && <p style={{ margin: 0, fontSize: 12.5, color: "var(--c-700)" }}>{strength.why}</p>}
          <AdjustedNote param={strength} />
          <StrippedEvidenceNote stripped={strength.evidence_stripped} />
        </Section>
      )}

      {priority && (
        <Section
          icon={ArrowDownRight}
          tone="var(--blue-600)"
          title={`Fix this next call — ${priority.label}`}
          right={<span style={{ fontSize: 13, fontWeight: 700, color: "var(--c-900)" }}>{priority.score}/10</span>}
        >
          <Quote heard={priority.heard} atSeconds={priority.at_seconds} callLink={call.call_link} onSeek={seek}
                 speaker={priority.quote_speaker} />
          {priority.why && <p style={{ margin: "0 0 6px", fontSize: 12.5, color: "var(--c-700)" }}>{priority.why}</p>}
          <AdjustedNote param={priority} />
          <StrippedEvidenceNote stripped={priority.evidence_stripped} />

          {priority.ten && (
            // The target, in the coach's own words. This is the part no bought
            // tool can render, because none of them have these sentences.
            <div style={{ fontSize: 12, color: "var(--c-700)", background: "var(--c-100, rgba(120,120,120,.08))",
                          borderRadius: 8, padding: "7px 10px", marginBottom: 6 }}>
              <span style={{ ...muted, fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".04em" }}>
                What a 10 sounds like
              </span>
              <div style={{ marginTop: 2 }}>{priority.ten}</div>
            </div>
          )}

          {priority.try_next && (
            <div style={{ fontSize: 12.5, color: "var(--c-800)" }}>
              <span style={{ ...muted, fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".04em" }}>
                Try next time
              </span>
              <div style={{ marginTop: 2 }}>→ {priority.try_next}</div>
            </div>
          )}
          {/* What has to show up on the next call for this to count as fixed
              (Skill Universe R4: observed → drill → proof). Checkable by a
              reviewer, so "I tried" and "it worked" are not the same claim. */}
          {priority.proof_next_call && (
            <div style={{ fontSize: 12.5, color: "var(--c-800)", marginTop: 6 }}>
              <span style={{ ...muted, fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".04em" }}>
                Proof on the next call
              </span>
              <div style={{ marginTop: 2 }}>✓ {priority.proof_next_call}</div>
            </div>
          )}
          {isAi && <RootOfTheFix callId={call.id} parameterKey={priority.key} />}
          <PlaybookLine link={playbookLinks[priority.key]} />
        </Section>
      )}

      {call.delivery && (
        <Section icon={Activity} tone="var(--c-600)" title="Your delivery">
          {/* Prose only. The raw acoustics that produced this sentence stay in
              the row's diagnostics column and are not serialised to the API. */}
          <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: "var(--c-700)" }}>{call.delivery}</p>
        </Section>
      )}

      <FloorChecks checks={call.measures?.floor_checks} callLink={call.call_link} onSeek={seek} />

      {!isRefused(call) && (
        <AllScores call={call} observed={observed} disputes={disputes} canAdjust={canAdjust}
                   canDispute={canDispute} onAdjusted={onAdjusted} onDisputed={onDisputed} />
      )}

      <MoreAboutThisCall parts={[
        hasMeasures && "how the conversation moved",
        unobserved.length > 0 && `${unobserved.length} skill${unobserved.length === 1 ? "" : "s"} not seen`,
        otherCalls > 0 && `${otherCalls} other call${otherCalls === 1 ? "" : "s"} with ${company}`,
      ].filter(Boolean)}>
        {hasMeasures && (
          <>
            {/* Group I — how the conversation moved. Band gauges, because these
                have a healthy range rather than a direction. */}
            <Section icon={Activity} tone="var(--c-600)" title="Call dynamics"
                     right={<span style={{ fontSize: 10, ...muted }}>measured, not judged</span>}>
              <DynamicsStrip measures={call.measures} priorMeasures={priorMeasures} calibrated={false} />
            </Section>

            {/* Group II — what was actually said. Counts and facts rather than
                positions on a scale, so they get their own treatment instead of
                being forced through a gauge that cannot express "none raised". */}
            <Section icon={MessageSquare} tone="var(--c-600)" title="What came up"
                     right={<span style={{ fontSize: 10, ...muted }}>counted from the transcript</span>}>
              <ContentSignals measures={call.measures} />
            </Section>
          </>
        )}

        {unobserved.length > 0 && (
          <Section icon={EyeOff} tone="var(--c-500)" title="Not seen on this call">
            {unobserved.map((p) => (
              <div key={p.key} style={{ fontSize: 12, ...muted, padding: "2px 0" }}>
                <span style={{ color: "var(--c-700)" }}>{p.label}</span>
                {p.why ? ` — ${p.why}` : p.reason === "not_applicable"
                  ? " — doesn't apply to this kind of call."
                  : p.reason === "outcome"
                  ? " — measured from what happens after the call, not from the call itself."
                  : " — no evidence either way on this one."}
              </div>
            ))}
          </Section>
        )}

        <CompanyHistory call={call} history={history} onOpenCall={onOpenCall} />
      </MoreAboutThisCall>
    </div>
  );
}
